import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import { DateTime } from "luxon";
import { RootState } from "typedefs";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import { setSelectedEvent, fetchEvents, clearEvents } from "app/actions/events";
import i18n from "app/i18n";

import Event from "../../Event";
import renderWeekViewHeader from "./renderDayHeader";

interface WeekViewProps {
  calendarRef: React.RefObject<any>;
  timeZone: string;
  preferences: CalendarPreferences;
  eventElRef: React.RefObject<any>;
  initialDate: DateTime;
  businessHours: any;
  onIntervalChange: (start: DateTime, end: DateTime) => void;
  onSelect: (info: any) => void;
  onUnselect: () => void;
  unselectCancel: string;
  snapDuration: string;
}

export default function WeekView({
  calendarRef,
  timeZone,
  preferences,
  eventElRef,
  initialDate,
  businessHours,
  onIntervalChange,
  onSelect,
  onUnselect,
  unselectCancel,
  snapDuration,
}: WeekViewProps) {
  const dispatch = useDispatch();
  const events = useSelector((state: RootState) => state.events);

  useEffect(() => {
    dispatch(clearEvents());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderEvent = useCallback(
    (info: any) => <Event {...info} timeZone={timeZone} />,
    [timeZone],
  );

  const renderDayHeader = useCallback(
    (info: any) => renderWeekViewHeader(info, { events: events.data as any[] }),
    [events.data],
  );

  const handleEventClick = useCallback(
    (info: any) => {
      (eventElRef as React.MutableRefObject<any>).current = info.el;
      dispatch(setSelectedEvent(parseInt(info.event.id, 10)));
    },
    [dispatch, eventElRef],
  );

  const handleDatesSet = useCallback(
    (dateInfo: any) => {
      const start = DateTime.fromJSDate(dateInfo.start).setZone(timeZone);
      const end = DateTime.fromJSDate(dateInfo.end).setZone(timeZone);
      onIntervalChange(start, end);
      dispatch(fetchEvents({ start: start.toISO(), end: end.toISO() }) as any);
    },
    [timeZone, onIntervalChange, dispatch],
  );

  return (
    <FullCalendar
      headerToolbar={false}
      ref={calendarRef}
      events={events.data as any[]}
      locale={i18n.language}
      allDaySlot={false}
      plugins={[timeGridPlugin, luxonPlugin, interactionPlugin]}
      initialView="timeGridWeek"
      initialDate={initialDate?.toJSDate()}
      timeZone={timeZone}
      height="100%"
      businessHours={businessHours}
      slotDuration="01:00:00"
      scrollTime="05:45:00"
      nowIndicator={true}
      nowIndicatorClassNames="now-indicator-line now-indicator-container"
      eventMinHeight={20}
      eventBackgroundColor="#039BE5"
      longPressDelay={600}
      firstDay={preferences.firstDay}
      selectMirror={true}
      unselectAuto={true}
      expandRows={true}
      selectable={true}
      unselectCancel={unselectCancel}
      select={onSelect}
      unselect={onUnselect}
      snapDuration={snapDuration}
      dayHeaderFormat={{ day: "numeric" as const, weekday: "short" as const }}
      slotLabelFormat={{
        hour: "numeric" as const,
        minute: "2-digit" as const,
        omitZeroMinute: true,
        hour12: preferences.timeFormat === "12h",
      }}
      eventTimeFormat={{
        hour: "numeric" as const,
        minute: "2-digit" as const,
        omitZeroMinute: true,
        hour12: preferences.timeFormat === "12h",
      }}
      eventContent={renderEvent}
      dayHeaderContent={renderDayHeader}
      eventClick={handleEventClick}
      datesSet={handleDatesSet}
    />
  );
}
